import {orderPatchReveals} from '../services/patchProgression';
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useGuest } from "../services/GuestContext";
import { useAuth } from "../services/authContext";
import { db } from "../services/db";
import { GuestApiError } from "../services/guestApi";
import { badgeRequest, BadgeAward, BadgeState, BadgeRarity as Rarity } from "../services/badgeApi";

import KeepsakeReveal from './KeepsakeReveal';
import BadgeRarity from './BadgeRarity';
import {presentations} from '../services/presentationCoordinator';
import {usePresentationState} from '../services/usePresentation';
import {useBadgeScope} from '../services/useBadgeScope';
import {useOnboarding} from '../services/OnboardingContext';
export default function BadgeCelebration() {
  const onboarding=useOnboarding();
  const location=useLocation(),presentation=usePresentationState();
  const lease=useRef<(()=>void)|null>(null);
  const [presenting,setPresenting]=useState(false);
  const [returnLabel,setReturnLabel]=useState('Back to the map');
  const { farm } = useGuest(),
    { user } = useAuth(),
    database = db;
  const scope = useBadgeScope();
  const routeCheck=useRef<(()=>Promise<void>)|null>(null);
  const [reveal, setReveal] = useState<{
      scope: string;
      awards: BadgeAward[];
      statistics?:Rarity[];
    } | null>(null),
    [error, setError] = useState("");
  const checking = useRef(false),
    currentScope = useRef(scope);
  currentScope.current = scope;
  const dismissed = useRef<{ scope: string; ids: Set<string> }>({
    scope,
    ids: new Set(),
  });
  const memoryAcknowledgements=useRef<{scope:string;ids:Set<string>}>({scope,ids:new Set()});
  const confirmAcknowledgement = async (key: string, ids: string[]) => {
    if (!farm) return;
    for (let i = 0; i < ids.length; i += 200) {
      const batch = ids.slice(i, i + 200);
      await badgeRequest(farm.farm_id, "/acknowledge", "POST", {
        award_ids: batch,
      });
      if (database !== db) return;
      if(memoryAcknowledgements.current.scope===scope)batch.forEach(id=>memoryAcknowledgements.current.ids.delete(id));
      await database.transaction("rw", database.workspaceMeta, async () => {
        const saved: string[] =
          (await database.workspaceMeta.get(key))?.value || [];
        await database.workspaceMeta.put({
          key,
          value: saved.filter((id) => !batch.includes(id)),
        });
      }).catch(()=>{/* Server acknowledgement succeeded; a stale local receipt can be retried safely. */});
    }
  };
  const allAwards =
    reveal?.scope === scope && user?.status === "Active" ? reveal.awards : [];
  const awards=orderPatchReveals(allAwards.filter(a=>!a.historical&&!(a.award_id===onboarding.state?.award?.award_id&&onboarding.state.award.acknowledged_at))),historical=allAwards.filter(a=>a.historical);
  useEffect(()=>{
    if(!onboarding.pending&&awards.length&&!presenting&&!presentation.blocked&&document.visibilityState==='visible'){
      const acquired=presentations.claim('recognition');
      if(acquired){lease.current=acquired;setReturnLabel(location.pathname==='/'?(Date.now()-presentation.returnLabelAt<8000?presentation.returnLabel:'Back to the map'):location.pathname.startsWith('/projects')?'Back to your project':location.pathname.startsWith('/gallery')?'Back to your photos':location.pathname==='/profile'?'Back to your profile':'Continue');setPresenting(true);}
    }
    if(!awards.length&&presenting){lease.current?.();lease.current=null;setPresenting(false);}
  },[awards.length,presenting,presentation,location.pathname,onboarding.pending]);
  useEffect(()=>()=>{lease.current?.();lease.current=null;},[scope]);
  const dismiss = async (targets=awards) => {
    if (!farm || (dismissed.current.scope===scope&&targets.every(a=>dismissed.current.ids.has(a.award_id)))) return;
    setError("");
    // Dismiss immediately, even when device storage or acknowledgement fails.
    if(dismissed.current.scope!==scope)dismissed.current={scope,ids:new Set()};
    if(memoryAcknowledgements.current.scope!==scope)memoryAcknowledgements.current={scope,ids:new Set()};
    targets.forEach(a=>dismissed.current.ids.add(a.award_id));
    targets.forEach(a=>memoryAcknowledgements.current.ids.add(a.award_id));
    setReveal(r=>r?.scope===scope?{...r,awards:r.awards.filter(a=>!targets.some(t=>t.award_id===a.award_id))}:r);
    if(!awards.some(a=>!targets.some(t=>t.award_id===a.award_id))){lease.current?.();lease.current=null;setPresenting(false);}
    try {
      const key = "badge-ack:" + farm.farm_id;
      await database.transaction("rw", database.workspaceMeta, async () => {
        const saved: string[] =
          (await database.workspaceMeta.get(key))?.value || [];
        await database.workspaceMeta.put({
          key,
          value: [...new Set([...saved, ...targets.map((a) => a.award_id)])],
        });
      }).catch(()=>{setError('Dismissed here. Device acknowledgement could not be saved.');});
      if (navigator.onLine) {
        const saved=(await database.workspaceMeta.get(key).catch(()=>null))?.value||[];
        const ids=[...new Set([...saved,...memoryAcknowledgements.current.ids])];
        await confirmAcknowledgement(key, ids);
      }
    } catch {
      setError("Dismissed here. Recognition acknowledgement will be retried when storage and connection are available.");
    }
  };

  useEffect(() => {
    let current = true;
    setReveal(null);
    lease.current?.();lease.current=null;setPresenting(false);
    setError("");
    dismissed.current = { scope, ids: new Set() };
    memoryAcknowledgements.current={scope,ids:new Set()};
    const check = async () => {
      if (
        !farm ||
        user?.status !== "Active" ||
        !navigator.onLine ||
        document.visibilityState !== "visible" ||
        checking.current
      )
        return;
      checking.current = true;
      try {
        const key = "badge-ack:" + farm.farm_id;
        const storedPending:string[]=(await database.workspaceMeta.get(key).catch(()=>null))?.value||[];
        const pending:string[]=[...new Set([...storedPending,...memoryAcknowledgements.current.ids])];
        if (pending.length) await confirmAcknowledgement(key, pending);
        const data = await badgeRequest<BadgeState>(farm.farm_id);
        if (current && database === db && currentScope.current === scope)
          setReveal({
            scope,
            statistics:data.statistics,
            awards: data.awards.filter(
              (a) =>
                !a.revoked_at &&
                !a.acknowledged_at &&
                !pending.includes(a.award_id) &&
                !dismissed.current.ids.has(a.award_id),
            ),
          });
      } catch (error) {
        if (
          current &&
          error instanceof GuestApiError &&
          [401, 403, 404].includes(error.status)
        )
          setReveal(
            null,
          ); /* Retry after reconnect; acknowledgements remain durable on this account. */
      } finally {
        checking.current = false;
      }
    };
    routeCheck.current=check;
    void check();
    const timer = setInterval(check, 20000);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      current = false;
      if(routeCheck.current===check)routeCheck.current=null;
      clearInterval(timer);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [scope, user?.status, database]);
  // A newly granted manual patch is checked on navigation as well as sign-in,
  // reconnect and polling. Keep dismissals/receipts intact across page visits.
  useEffect(()=>{void routeCheck.current?.();},[location.key,location.pathname]);
  if (onboarding.pending) return null;
  if (!awards.length&&historical.length) return <aside role="status" className="fixed bottom-5 right-5 z-[400] max-w-xs rounded-lg border bg-white p-4 text-sm text-gray-700"><p>{historical.length} {historical.length===1?'record was':'records were'} added to your recognition history.</p><Link className="underline inline-block mt-2" to="/profile" onClick={()=>void dismiss(historical)}>View my collection</Link><button className="ml-3 underline" onClick={()=>void dismiss(historical)}>Dismiss</button></aside>;
  if (!awards.length) return error?<div role="status" className="fixed bottom-5 right-5 z-[400] max-w-xs rounded-lg border bg-white p-3 text-sm text-gray-700">{error}<button className="ml-2 underline" onClick={()=>setError('')}>Dismiss</button></div>:null;
  if(!presenting)return <div role="status" className="fixed bottom-24 right-5 z-[400] rounded-lg bg-white/95 border px-3 py-2 text-xs text-usda-800">Recognition is waiting for a quiet moment.</div>;
  const first=awards[0];
  return <KeepsakeReveal key={first.award_id} owner="recognition" level={first.level} property={farm?.name} title={first.display_name||first.name} description={first.description} image={first.display_image||first.image} medal={first.presentation_kind==='medal'} returnLabel={awards.length>1?'Next patch':returnLabel} onDismiss={()=>void dismiss([first])} onClose={()=>void dismiss(awards)} onSecondary={()=>void dismiss(awards)} secondaryTo="/community/badges" secondaryLabel={awards.length>1?'Skip to my patches':'View my patches'}>
   {first.story&&<p className="text-sm text-gray-600 whitespace-pre-wrap">{first.story}</p>}
   <BadgeRarity value={reveal?.statistics?.find(s=>s.badge_id===first.badge_id)}/>
   {awards.length>1&&<p className="text-xs text-gray-500">{awards.length-1} more {awards.length===2?'patch':'patches'} after this</p>}
  </KeepsakeReveal>;
}
